import Minze, { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { config, Font, SCREEN_WIDTHS } from '../../../constants'
import { Autocomplete, IAutocompleteItem } from '../../../modules/autocomplete'
import { Callback } from '../../../modules/callback'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import { AbstractSearchCore, ISearchConfig } from '../../../modules/search'
import { SearchBarSubscriber } from '../../../modules/search/SearchBarSubscriber'
import {
  delay,
  escapeHtml,
  generateId,
  getVisibleWidth,
  unescapeHtml,
} from '../../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'searchBar', {
  searchPlaceholder: 'Rechercher',
  autocompleteTitle: 'Recherches suggérées',
  allEvents: 'Tous les événements',
  allExhibitions: 'Toutes les expositions',
  allFestivals: 'Tous les festivals',
  allEventSeries: 'Tous les festivals et séries',
  allArtists: 'Tous les artistes',
  allPlaces: 'Tous les lieux',
})

Locale.getInstance().registerKeySet('en', 'searchBar', {
  searchPlaceholder: 'Search for an event, artist or venue',
  autocompleteTitle: 'Suggested searches',
  allEvents: 'All events',
  allExhibitions: 'All exhibitions',
  allFestivals: 'All festivals',
  allEventSeries: 'All festivals and series',
  allArtists: 'All artists',
  allPlaces: 'All places',
})

export interface LvcSearchBar {
  dataGroupId?: string
  dataGoToSearch?: boolean
  query: string
}

export class LvcSearchBar extends MinzeElement {
  private _core: AbstractSearchCore | null = null
  clickCb = Callback.attach(this.onButtonClick.bind(this))
  clickCbDropdown = Callback.attach(this.onDropdownClick.bind(this))
  allEventsClickCb = Callback.attach(this.onAllButtonClick.bind(this))
  attrs: Attrs = ['data-group-id', ['data-go-to-search', true]]
  reactive: Reactive = [['query', '', true]]

  searchBarSubscriber: SearchBarSubscriber | null = null
  focused: boolean = false
  autocompleteList: IAutocompleteItem[] = []
  defaultQuery: string = ''

  private toggleCb: string = ''
  private dropdownModalId = generateId()

  private debounceTimer: NodeJS.Timeout | null = null

  public async onChange(event: any): Promise<void> {
    await this.updateInput(event.target.value)
  }

  // This function is called when the user clicks on a suggestion
  public onButtonClick(value: string): void {
    let json = JSON.parse(decodeURIComponent(value))
    //redirect to the event/artist/exhibition page itself
    MainModule.getInstance().getRouting().goTo(json.value)
    this.focused = false
    this.autoCompleteList = []
    this.execRender()
  }

  // function called when the user clicks on one of the button like 'see all events' or 'see all exhibitions'
  public async onAllButtonClick(value: string) {
    let modal = value
    await this.searchBarSubscriber?.updateQuery(this.query, modal)
    this.searchBarSubscriber?.goToSearch(true)
  }

  public onDropdownClick() {
    MainModule.getInstance().showModal(this.dropdownModalId)
  }

  // that function is called when the user types in the input, it updates the query state and fetches the suggestions
  public async updateInput(value: string): Promise<void> {
    let inputElement = this.select('[name="query"]')

    if (!inputElement) {
      return
    }
    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    // Set new timer with 300ms delay
    this.debounceTimer = setTimeout(async () => {
      await this.searchBarSubscriber?.updateQuery(value)
      this.query = value
      await this.updateAutocompletion()
    }, 500)
  }

  public async updateAutocompletion(): Promise<void> {
    let list = await Autocomplete.instance.getSuggestions(this.query)
    this.autocompleteList = list
    this.execRender()
  }

  toggleDrawer = () => {
    return this.focused ? 'show' : ''
  }

  toggleFocus = () => {
    return this.focused ? 'data-focused="true"' : ''
  }

  onBlurMobile(event: any): void {
    if (getVisibleWidth() > SCREEN_WIDTHS.TABLET) {
      return
    }
    delay(0).then(() => {
      this.focused = false
      this.execRender()
    })
  }

  /*
  Andrei made that a while ago, it's a function that is called when the user clicks outside of the search bar
  it was made to close the dropdown when the user clicks outside of the search bar
  apparently it was the only way to make it work with the suggestions and the dropdown
  */
  public async onBlur(event: any): Promise<void> {
    // Concurency issue with the click event on the lvc-search-suggestion button
    await delay(0)

    // check wether the click is in the clientBoundingRect of the element
    let input = this.select('[name="query"]')
    let dropdownElement = this.select(`[modal-id="${this.dropdownModalId}"]`)
    if (!input || !dropdownElement) {
      return
    }

    let clientRect = input.getBoundingClientRect()
    let clientDropdownRect = dropdownElement.getBoundingClientRect()

    let clickedInside = false

    let x = event.clientX
    let y = event.clientY

    if (
      x >= clientRect.left &&
      x <= clientRect.right &&
      y >= clientRect.top &&
      y <= clientRect.bottom
    ) {
      clickedInside = true
    }
    if (
      x >= clientDropdownRect.left &&
      x <= clientDropdownRect.right &&
      y >= clientDropdownRect.top &&
      y <= clientDropdownRect.bottom
    ) {
      clickedInside = true
    }

    if (!clickedInside) {
      this.focused = false
      this.autocompleteList = []
      MainModule.getInstance().hideModal(this.dropdownModalId)
      this.execRender()
    }
  }

  public onFocus(event: any): void {
    delay(0).then(() => {
      this.focused = true
      // call dropdown callback
      this.toggleFocus()
      this.onDropdownClick()
      this.execRender()
    })
  }

  public search(): void {
    this.searchBarSubscriber?.doSearch(this.query)
  }

  public async flushQueryAndExecute(): Promise<void> {
    // Cancel pending debounce so we don't apply a stale value after navigation
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
    // Flush the latest input value immediately to the search core
    const inputEl = this.select('[name="query"]') as HTMLInputElement | null
    const value = inputEl?.value ?? this.query
    await this.searchBarSubscriber?.updateQuery(value)
    this.query = value
  }

  public async onEnterKey(event: KeyboardEvent): Promise<void> {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (this.focused) {
        Minze.cast('lvc:search-execution-requested')
      }
    }
  }

  public async handleSubscriberUpdate(searchConfig: ISearchConfig) {
    let inputElement = this.select('[name="query"]')
    if (!inputElement) {
      return
    }
    let value = unescapeHtml(searchConfig?.query || '')
    this.defaultQuery = value
    this.query = value
    if (this.focused) {
      await this.updateAutocompletion()
      this.execRender()
    }
  }

  async clearInput() {
    this.query = ''
    this.rerender(true)
    await this.updateInput('')
  }

  onReady(): void | Promise<void> {
    this.searchBarSubscriber = new SearchBarSubscriber(
      this.dataGroupId as string,
      this.handleSubscriberUpdate.bind(this)
    )
    const mainModule = MainModule.getInstance()
    mainModule.getSearchManager().subscribe(this.searchBarSubscriber)
    this.toggleCb = Callback.attach(this.onToggle.bind(this))
  }

  async onToggle(state: boolean) {
    this.isOpen = state
    this.rerender()
  }

  execRender(): void {
    this.rerender()
  }

  onDestroy(): void {
    Callback.detach(this.clickCb)
  }

  renderList(): string {
    let gr = this.dataGroupId ? `data-group-id="${this.dataGroupId}"` : ''
    return this.autocompleteList
      .map((item: IAutocompleteItem) => {
        return `<lvc-search-suggestion on-click="${this.clickCb}" ${gr} data-value="${encodeURIComponent(JSON.stringify(item))}">
        <span>${item.label}</span>
      </lvc-search-suggestion>`
      })
      .join('')
  }

  renderSuggestionDropdownDesktop(): string {
    return /*html*/ `
    <lvc-dropdown modal-id="${this.dropdownModalId}" on-toggle="${this.toggleCb}" no-overlay="true">
        <div
          class="search-suggestions-dropdown"
          slot="lvc-dropdown-content"
        >
        ${this.renderList()}
          <div class="allButtons">
            <lvc-simple-button on-click="${this.allEventsClickCb}" data-value="${config.searchAllModals.festivals}"  btn-preset="searchAll"><lvc-search-two-icon class="searchAllIcons"> </lvc-search-two-icon><div style="color:#FF4600;font-weight:bold;font-size:16px;display:flex;gap:4px;align-items:center">${Locale.getKey('searchBar.allFestivals')}<lvc-festival-icon></lvc-festival-icon></div></lvc-simple-button>
            <lvc-simple-button on-click="${this.allEventsClickCb}" data-value="${config.searchAllModals.events}" btn-preset="searchAll"><lvc-search-two-icon class="searchAllIcons"> </lvc-search-two-icon>${Locale.getKey('searchBar.allEvents')}</lvc-simple-button>
            <lvc-simple-button on-click="${this.allEventsClickCb}" data-value="${config.searchAllModals.exhibitions}"  btn-preset="searchAll"><lvc-search-two-icon class="searchAllIcons"> </lvc-search-two-icon>${Locale.getKey('searchBar.allExhibitions')}</lvc-simple-button>
            <lvc-simple-button on-click="${this.allEventsClickCb}" data-value="${config.searchAllModals.artists}"  btn-preset="searchAll"><lvc-search-two-icon class="searchAllIcons"> </lvc-search-two-icon>${Locale.getKey('searchBar.allArtists')}</lvc-simple-button>
            <lvc-simple-button on-click="${this.allEventsClickCb}" data-value="${config.searchAllModals.places}"  btn-preset="searchAll"><lvc-search-two-icon class="searchAllIcons"> </lvc-search-two-icon>${Locale.getKey('searchBar.allPlaces')}</lvc-simple-button>
          </div>
        </div>

      </lvc-dropdown>
    `
  }

  renderSuggestionDropdownMobile(): string {
    return /*html*/ `
    <div class="autocomplete-drawer ${this.toggleDrawer()}" data-show="${this.toggleDrawer()}">
    <div class="autocomplete-drawer-title">${Locale.getKey('searchBar.autocompleteTitle')}</div>
    <div class="autocomplete-drawer-items" slot="items">
      <slot>
        ${this.renderList()}
      </slot>
    </div>
    <div class="allButtons">
    <lvc-simple-button on-click="${this.allEventsClickCb}" data-value="${config.searchAllModals.festivals}"  btn-preset="searchAll"><lvc-search-two-icon class="searchAllIcons"> </lvc-search-two-icon><div style="color:#FF4600;font-weight:bold;font-size:16px;display:flex;gap:4px;align-items:center">${Locale.getKey('searchBar.allFestivals')}<lvc-festival-icon></lvc-festival-icon></div></lvc-simple-button>
    <lvc-simple-button on-click="${this.allEventsClickCb}" data-value="${config.searchAllModals.events}" btn-preset="searchAll"><lvc-search-two-icon class="searchAllIcons"> </lvc-search-two-icon>${Locale.getKey('searchBar.allEvents')}</lvc-simple-button>
    <lvc-simple-button on-click="${this.allEventsClickCb}" data-value="${config.searchAllModals.exhibitions}"  btn-preset="searchAll"><lvc-search-two-icon class="searchAllIcons"> </lvc-search-two-icon>${Locale.getKey('searchBar.allExhibitions')}</lvc-simple-button>
    <lvc-simple-button on-click="${this.allEventsClickCb}" data-value="${config.searchAllModals.artists}"  btn-preset="searchAll"><lvc-search-two-icon class="searchAllIcons"> </lvc-search-two-icon>${Locale.getKey('searchBar.allArtists')}</lvc-simple-button>
    <lvc-simple-button on-click="${this.allEventsClickCb}" data-value="${config.searchAllModals.places}"  btn-preset="searchAll"><lvc-search-two-icon class="searchAllIcons"> </lvc-search-two-icon>${Locale.getKey('searchBar.allPlaces')}</lvc-simple-button>
  </div>
  </div>
    `
  }

  html(): string {
    const isMobile = getVisibleWidth() <= SCREEN_WIDTHS.TABLET
    return /*html*/ `
      <div name="searchBar" class="searchBar">
      <div class="search-box" data-focused='${this.focused}'>
        <lvc-search-icon name="search-icon" data-size="20" color="#1E007D"></lvc-search-icon>
        <input
        type="text"
        autocomplete="off"
        class="search-input"
        name="query"
        id="query"
        placeholder="${Locale.getKey(
          window.matchMedia('(max-width: 730px)').matches
            ? 'searchBar.searchPlaceholderMobile'
            : 'searchBar.searchPlaceholder'
        )}"
        value="${escapeHtml(this.query)}"
        />
        <button id="clear-button" type="button" class="${this.query ? '' : 'hidden'}">
          <lvc-search-cross-icon></lvc-search-cross-icon>
        </button>
      </div>
      ${isMobile ? this.renderSuggestionDropdownMobile() : this.renderSuggestionDropdownDesktop()}
      </div>
    `
  }

  css = () => {
    return /*css*/ `
      :host {
      }
      
      .search-box {
        position: relative;
      }

      #clear-button {
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        background: none;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }
      
      #clear-button.hidden {
        opacity: 0;
        pointer-events: none;
      }
      
      #clear-button > lvc-search-cross-icon {
        width: 16px;
        height: 16px;
      }
      
      @media screen and (min-width: 768px) {
        #clear-button {
          right: 10px;
        }
      }

      .search-box {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 54px;
        border-radius: 50px;
        overflow: hidden;
        background: white;
        padding: 0 10px 0 15px;
        border: ${this.focused ? '1px solid #1E007D' : '1px solid white'};
      }

      .search-input {
        font-size: 16px;
        font-weight: ${Font.weight.medium};
        font-family: inherit;
        margin-top: 0;
        background: unset;
        color: #1E007D;
        padding: 14px 10px;
        border: none;
        outline: none;
        flex: 1;
      }
      
      lvc-search-icon {
        height: 20px;
      }

      .clear-input-button-container{
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1;
        margin-left: 10px;
      }

      .clear-input-button {
        display: flex;
        cursor: pointer;
        align-items: center;
        width: 28px;
        height: 28px;
        background: transparent;
        border: none;
      }

      .searchAllIcons{
        margin-right: 10px;
      }

      .allButtons{
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin-top: 10px;
      }
      .allButtons:first-child{
        margin-top: 0px;
      }

      .allButtons lvc-simple-button, lvc-search-suggestion {
        transition: transform 0.3s ease-in-out, background 0.3s ease-in-out; /* Add a smooth transition effect for transform and background */
      }

      .allButtons lvc-simple-button:hover, lvc-search-suggestion:hover {
        transform: translateX(5px); /* Move the button 5 pixels to the right on hover */
        background: var(--brand-menthe, #DCFFD7);
      }

      lvc-drawer .search-suggestions-dropdown {
        background: transparent;
        box-shadow: none;
        border: none;
        width: auto;
      }

      .search-box lvc-search-two-icon {
        margin-left: 10px;
      }

      .autocomplete-drawer {
        display: none;
        padding-top: 10px;
      }

      .autocomplete-drawer.show {
        display: block;
      }

      .autocomplete-drawer-title {
        color: var(--brand-minuit, #1E007D);
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;
        /* D/H/H6 */
        font-family: ${Font.family};
        font-size: 24px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 120%; /* 28.8px */
      }

      lvc-search-suggestion {
        border-bottom: 1px solid var(--faded-minuit-30, rgba(30, 0, 125, 0.30));
      }


      @media only screen and (max-width: 991px) {
        .search-box {
          border: none;
          border-radius: 0px;
          border-bottom: 1px solid var(--brand-minuit, #1E007D);
          padding: 0 4px;

        }
        .search-input {
          font-size: 18px;
          font-weight: ${Font.weight.extraBold};
        }
        .search-input::placeholder {
          color: #1E007D;
        }
        .search-input::-ms-input-placeholder {
          color: #1E007D;
        }
        [data-focused="true"] .search-box {
          background: var(--brand-menthe, #DCFFD7);
          border-radius: 50px;
         }
        .search-box lvc-search-two-icon {
          margin-left: 0px;
        }
      }
    `
  }

  eventListeners: EventListeners = [
    ['[name="query"]', 'focus', this.onFocus.bind(this)],
    [window, 'click', this.onBlur.bind(this)],
    [window, 'blur', this.onBlurMobile.bind(this)],
    ['[name="query"]', 'input', this.onChange.bind(this)],
    ['[name="query"]', 'keydown', this.onEnterKey.bind(this)],
    [window, 'lvc:clear-search', this.clearInput.bind(this)],
    ['#clear-button', 'click', this.clearInput.bind(this)],
  ]
}
